import * as i0 from '@angular/core';
import { forwardRef, Component, ChangeDetectionStrategy, Self, ViewChildren, Input } from '@angular/core';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i1 from '@itero/ui-toolkit-angular/core';
import { NgControlDirective, TlkErrorStateMatcherDirective, TlkIsDisabledDirective } from '@itero/ui-toolkit-angular/core';
import * as i3 from '@angular/forms';
import { Validators, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { TlkFormFieldComponent } from '@itero/ui-toolkit-angular/form-field';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { CloseEmptyIconComponent } from '@itero/ui-toolkit-angular/icons';

let nextUniqueId = 0;
class TlkTextInputComponent {
    /** Unique id of the element */
    set id(value) {
        this._id = value;
    }
    get id() {
        return this._id;
    }
    /** Whether the component is required. */
    get required() {
        return this._required ?? this.ngControlDirective.control?.hasValidator(Validators.required) ?? false;
    }
    set required(value) {
        this._required = coerceBooleanProperty(value);
    }
    get _labelId() {
        return this.id + '-label';
    }
    get _inputId() {
        return this.id + '-input';
    }
    /** Whether the select is disabled. */
    get disabled() {
        return this.isDisabledDirective.isDisabled;
    }
    get _errorState() {
        return this.errorStateMatcherDirective.errorState;
    }
    get empty() {
        return this._value == null || this._value == '';
    }
    get _labelAbove() {
        return !this.empty || this.__focused || !!this.placeholder;
    }
    constructor(_changeDetectorRef, ngControlDirective, errorStateMatcherDirective, isDisabledDirective) {
        this._changeDetectorRef = _changeDetectorRef;
        this.ngControlDirective = ngControlDirective;
        this.errorStateMatcherDirective = errorStateMatcherDirective;
        this.isDisabledDirective = isDisabledDirective;
        /** Label of component, default is '', strongly recommended to be filled */
        this.label = '';
        /** Placeholder of component, rendered in the place of the value when no selections/input, If placeholder is '' then Label is doing its function by floating*/
        this.placeholder = '';
        /** Whether the input contains "X" icon to clear the field. It clears with empty */
        this.cancelable = false;
        /** Name assigned to input element inside*/
        this.name = '';
        this.autocomplete = null;
        this.__focused = false;
        this._id = `tlk-text-input-${nextUniqueId++}`;
        /** `View -> model callback called when value changes` */
        this._onChange = () => { };
        /** `View -> model callback called when select has been touched` */
        this._onTouched = () => { };
    }
    /** Value of the select control. */
    // @Input() // out of order when assigned as Input
    get _value() {
        return this.__value;
    }
    set _value(newValue) {
        const hasAssigned = this._assignValue(newValue);
        if (hasAssigned) {
            this._onChange(newValue);
        }
    }
    /** Whether the select is focused. */
    get _focused() {
        return this.__focused;
    }
    _onFocus() {
        if (!this.disabled) {
            this.__focused = true;
        }
    }
    /**
     * Calls the touched callback only if the panel is closed. Otherwise, the trigger will
     * "blur" to the panel when it opens, causing a false positive.
     */
    _onBlur() {
        this.__focused = false;
        console.log('blur');
        console.log(document.activeElement);
        if (!this.disabled) {
            this._onTouched();
            this._changeDetectorRef.markForCheck();
        }
    }
    _focusInput(input) {
        if (!this.disabled) {
            input.focus();
        }
    }
    _cancelSelection(event) {
        event.stopPropagation();
        if (!this.disabled) {
            this._value = '';
        }
    }
    /**
     * Sets the input's value. Part of the ControlValueAccessor interface
     * required to integrate with Angular's core forms API.
     *
     * @param value New value to be written to the model.
     */
    writeValue(value) {
        this._assignValue(value);
    }
    /**
     * Saves a callback function to be invoked when the input's value
     * changes from user input. Part of the ControlValueAccessor interface
     * required to integrate with Angular's core forms API.
     *
     * @param fn Callback to be triggered when the value changes.
     */
    registerOnChange(fn) {
        this._onChange = fn;
    }
    /**
     * Saves a callback function to be invoked when the input is blurred
     * by the user. Part of the ControlValueAccessor interface required
     * to integrate with Angular's core forms API.
     *
     * @param fn Callback to be triggered when the component has been touched.
     */
    registerOnTouched(fn) {
        this._onTouched = fn;
    }
    /**
     * Disables the input. Part of the ControlValueAccessor interface required
     * to integrate with Angular's core forms API.
     *
     * @param isDisabled Sets whether the component is disabled.
     */
    setDisabledState(isDisabled) {
        this.isDisabledDirective.setDisabledState(isDisabled);
        this._changeDetectorRef.markForCheck();
    }
    modelChangeHandler(value) {
        this._assignValue(value);
        this._onChange(value);
    }
    /** Assigns a specific value to the select. Returns whether the value has changed. */
    _assignValue(newValue) {
        if (newValue !== this.__value) {
            this.__value = newValue;
            this._changeDetectorRef.markForCheck();
            return true;
        }
        return false;
    }
}
TlkTextInputComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: TlkTextInputComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: i1.NgControlDirective, self: true }, { token: i1.TlkErrorStateMatcherDirective, self: true }, { token: i1.TlkIsDisabledDirective, self: true }], target: i0.ɵɵFactoryTarget.Component });
TlkTextInputComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: TlkTextInputComponent, isStandalone: true, selector: "tlk-text-input", inputs: { id: "id", label: "label", placeholder: "placeholder", cancelable: "cancelable", name: "name", autocomplete: "autocomplete", required: "required" }, host: { properties: { "class.tlk-text-input_required": "required", "class.tlk-text-input_disabled": "disabled", "class.tlk-text-input_invalid": "_errorState", "attr.id": "id", "attr.data-testid": "id" }, classAttribute: "tlk-text-input" }, providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TlkTextInputComponent),
            multi: true
        }
    ], viewQueries: [{ propertyName: "inputElement", predicate: ["inputElement"], descendants: true }], hostDirectives: [{ directive: i1.NgControlDirective }, { directive: i1.TlkErrorStateMatcherDirective, inputs: ["errorStateMatcher", "errorStateMatcher"] }, { directive: i1.TlkIsDisabledDirective, inputs: ["isDisabled", "isDisabled"], outputs: ["disableChangeEvent", "disableChangeEvent"] }], ngImport: i0, template: "<tlk-form-field\n\t[label]=\"label\"\n\t[labelAbove]=\"_labelAbove\"\n\t[labelFor]=\"_inputId\"\n\t[labelId]=\"_labelId\"\n\t[focused]=\"_focused\"\n\t[required]=\"required\"\n\t(click)=\"_focusInput(inputElement)\"\n>\n\t<input\n\t\t#inputElement\n\t\tclass=\"tlk-text-input__input\"\n\t\t[required]=\"required\"\n\t\t[disabled]=\"disabled\"\n\t\t[attr.aria-invalid]=\"_errorState\"\n\t\t[attr.aria-disabled]=\"disabled.toString()\"\n\t\t[attr.autocomplete]=\"autocomplete\"\n\t\t[placeholder]=\"placeholder\"\n\t\t[name]=\"name\"\n\t\t[attr.name]=\"name\"\n\t\ttype=\"text\"\n\t\t[id]=\"_inputId\"\n\t\t[value]=\"_value\"\n\t\t[ngModel]=\"_value\"\n\t\t(ngModelChange)=\"modelChangeHandler($event)\"\n\t\t(focus)=\"_onFocus()\"\n\t\t(blur)=\"_onBlur()\"\n\t/>\n\t<ng-content></ng-content>\n\t<tlk-close-empty-icon\n\t\t*ngIf=\"cancelable && !disabled\"\n\t\t(click)=\"_cancelSelection($event)\"\n\t\tclass=\"tlk-text-input__cancel-icon tlk-icon tlk-icon_size_l\"\n\t\t[ngClass]=\"{'tlk-text-input__cancel-icon_hidden': empty || !_focused }\"\n\t\t[attr.aria-hidden]=\"empty\"\n></tlk-close-empty-icon>\n\t<ng-container tlk-error>\n\t\t<ng-content select=\"[tlk-error]\"></ng-content>\n\t</ng-container>\n\t<ng-container tlk-hint>\n\t\t<ng-content select=\"[tlk-hint]\"></ng-content>\n\t</ng-container>\n</tlk-form-field>\n", styles: [":host,:host *,:host :before,:host :after{box-sizing:border-box}:host{min-width:206px;max-width:100%;transition:color .2s ease-out}.tlk-text-input__input{display:block;align-self:flex-end;height:28px;box-sizing:border-box;padding-block:0;padding-inline:0;padding:1px 0;margin:0;max-width:100%;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;background:transparent;color:inherit;border:none;outline:none;font:inherit}.tlk-text-input__cancel-icon{align-self:center;margin-left:auto;color:inherit;opacity:1;transition:opacity .2s ease-out,transform .2s ease-out;transform:translate(0)}:host.tlk-text-input_invalid ::ng-deep .tlk-text-input__cancel-icon{color:var(--tlk-red-80)}.tlk-text-input__cancel-icon_hidden{opacity:0;transition:opacity .2s ease-in,transform .2s ease-in;transform:translate(25%)}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: TlkFormFieldComponent, selector: "tlk-form-field", inputs: ["labelId", "id", "label", "labelFor", "labelAbove", "focused", "required"] }, { kind: "ngmodule", type: FormsModule }, { kind: "directive", type: i3.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i3.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i3.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { kind: "directive", type: i3.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: CloseEmptyIconComponent, selector: "tlk-close-empty-icon" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: TlkTextInputComponent, decorators: [{
            type: Component,
            args: [{ selector: 'tlk-text-input', standalone: true, imports: [CommonModule, TlkFormFieldComponent, FormsModule, CloseEmptyIconComponent], changeDetection: ChangeDetectionStrategy.OnPush, hostDirectives: [
                        NgControlDirective,
                        {
                            directive: TlkErrorStateMatcherDirective,
                            inputs: ['errorStateMatcher']
                        },
                        {
                            directive: TlkIsDisabledDirective,
                            inputs: ['isDisabled'],
                            outputs: ['disableChangeEvent']
                        }
                    ], providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => TlkTextInputComponent),
                            multi: true
                        }
                    ], host: {
                        class: 'tlk-text-input',
                        '[class.tlk-text-input_required]': 'required',
                        '[class.tlk-text-input_disabled]': 'disabled',
                        '[class.tlk-text-input_invalid]': '_errorState',
                        '[attr.id]': 'id',
                        '[attr.data-testid]': 'id'
                    }, template: "<tlk-form-field\n\t[label]=\"label\"\n\t[labelAbove]=\"_labelAbove\"\n\t[labelFor]=\"_inputId\"\n\t[labelId]=\"_labelId\"\n\t[focused]=\"_focused\"\n\t[required]=\"required\"\n\t(click)=\"_focusInput(inputElement)\"\n>\n\t<input\n\t\t#inputElement\n\t\tclass=\"tlk-text-input__input\"\n\t\t[required]=\"required\"\n\t\t[disabled]=\"disabled\"\n\t\t[attr.aria-invalid]=\"_errorState\"\n\t\t[attr.aria-disabled]=\"disabled.toString()\"\n\t\t[attr.autocomplete]=\"autocomplete\"\n\t\t[placeholder]=\"placeholder\"\n\t\t[name]=\"name\"\n\t\t[attr.name]=\"name\"\n\t\ttype=\"text\"\n\t\t[id]=\"_inputId\"\n\t\t[value]=\"_value\"\n\t\t[ngModel]=\"_value\"\n\t\t(ngModelChange)=\"modelChangeHandler($event)\"\n\t\t(focus)=\"_onFocus()\"\n\t\t(blur)=\"_onBlur()\"\n\t/>\n\t<ng-content></ng-content>\n\t<tlk-close-empty-icon\n\t\t*ngIf=\"cancelable && !disabled\"\n\t\t(click)=\"_cancelSelection($event)\"\n\t\tclass=\"tlk-text-input__cancel-icon tlk-icon tlk-icon_size_l\"\n\t\t[ngClass]=\"{'tlk-text-input__cancel-icon_hidden': empty || !_focused }\"\n\t\t[attr.aria-hidden]=\"empty\"\n></tlk-close-empty-icon>\n\t<ng-container tlk-error>\n\t\t<ng-content select=\"[tlk-error]\"></ng-content>\n\t</ng-container>\n\t<ng-container tlk-hint>\n\t\t<ng-content select=\"[tlk-hint]\"></ng-content>\n\t</ng-container>\n</tlk-form-field>\n", styles: [":host,:host *,:host :before,:host :after{box-sizing:border-box}:host{min-width:206px;max-width:100%;transition:color .2s ease-out}.tlk-text-input__input{display:block;align-self:flex-end;height:28px;box-sizing:border-box;padding-block:0;padding-inline:0;padding:1px 0;margin:0;max-width:100%;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;background:transparent;color:inherit;border:none;outline:none;font:inherit}.tlk-text-input__cancel-icon{align-self:center;margin-left:auto;color:inherit;opacity:1;transition:opacity .2s ease-out,transform .2s ease-out;transform:translate(0)}:host.tlk-text-input_invalid ::ng-deep .tlk-text-input__cancel-icon{color:var(--tlk-red-80)}.tlk-text-input__cancel-icon_hidden{opacity:0;transition:opacity .2s ease-in,transform .2s ease-in;transform:translate(25%)}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i1.NgControlDirective, decorators: [{
                    type: Self
                }] }, { type: i1.TlkErrorStateMatcherDirective, decorators: [{
                    type: Self
                }] }, { type: i1.TlkIsDisabledDirective, decorators: [{
                    type: Self
                }] }]; }, propDecorators: { inputElement: [{
                type: ViewChildren,
                args: ['inputElement']
            }], id: [{
                type: Input
            }], label: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], cancelable: [{
                type: Input
            }], name: [{
                type: Input
            }], autocomplete: [{
                type: Input
            }], required: [{
                type: Input
            }] } });

/**
 * Generated bundle index. Do not edit.
 */

export { TlkTextInputComponent };
